import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList, faChevronLeft, faSearch, faFilter, faTimes, faBars, faSpinner, faSync } from '@fortawesome/free-solid-svg-icons';
import { useProject } from '../../../contexts/ProjectContext';
import brandConfig from '../../../config/brandConfig';
import { getCurrencySymbol } from '../../../utils/countryUtils';

const LOTS_PER_PAGE = 50;

const LeftSidebar = ({ onLotClick, mapInstance, onSelectLotFromMap }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedLotId, setSelectedLotId] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const { project, projectId, selectedLotData } = useProject();
  const lotsCache = useRef({});
  const listContainerRef = useRef(null);
  const lotCardRefs = useRef({});

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [minArea, setMinArea] = useState('');
  const [maxArea, setMaxArea] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Build query params for API
  const buildQueryParams = useCallback((skip = 0) => {
    const params = new URLSearchParams();
    params.append('skip', skip);
    params.append('limit', LOTS_PER_PAGE);
    params.append('show_prices', project?.show_prices || false);

    if (debouncedSearchTerm) {
      params.append('search', debouncedSearchTerm);
    }
    if (statusFilter) {
      // "Vendido" agrupa EN_VENTA, VENDIDO y DEVOLUCION para la vista del cliente
      const statusValue = statusFilter === 'VENDIDO' ? 'VENDIDO,EN_VENTA,DEVOLUCION' : statusFilter;
      params.append('status', statusValue);
    }
    if (minArea) {
      params.append('min_area', minArea);
    }
    if (maxArea) {
      params.append('max_area', maxArea);
    }
    if (minPrice && project?.show_prices) {
      params.append('min_price', minPrice);
    }
    if (maxPrice && project?.show_prices) {
      params.append('max_price', maxPrice);
    }

    return params.toString();
  }, [project?.show_prices, debouncedSearchTerm, statusFilter, minArea, maxArea, minPrice, maxPrice]);

  // Fetch lots from backend
  const fetchLots = useCallback(async (skip = 0, append = false) => {
    if (!projectId) return;

    // Si el proyecto está en EDICION o ARCHIVADO, no mostrar lotes
    if (project?.status === 'EDICION' || project?.status === 'ARCHIVADO') {
      setLots([]);
      setTotal(0);
      setHasMore(false);
      return;
    }

    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const apiUrl = brandConfig.apiUrl;
      const queryParams = buildQueryParams(skip);
      const fetchUrl = `${apiUrl}/projects/${projectId}/lots/public?${queryParams}`;
      const response = await fetch(fetchUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch lots: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.data && data.data.length > 0) {
        const lotsData = data.data.map((lot) => {
          let centroid_lng = null;
          let centroid_lat = null;

          if (lot.centroid) {
            const match = lot.centroid.match(/POINT\(([+-]?\d+\.?\d*)\s+([+-]?\d+\.?\d*)\)/);
            if (match) {
              centroid_lng = parseFloat(match[1]);
              centroid_lat = parseFloat(match[2]);
            }
          }

          return {
            id: lot.id,
            lot_number: lot.lot_number,
            status: lot.status || 'DISPONIBLE',
            area: lot.area_calculated || lot.area_declared || null,
            area_declared: lot.area_declared || null,
            area_calculated: lot.area_calculated || null,
            price: lot.price || null,
            currency: lot.currency || 'USD',
            centroid_lng,
            centroid_lat,
            geometry: null,
            ...lot
          };
        });

        if (append) {
          setLots(prev => [...prev, ...lotsData]);
        } else {
          setLots(lotsData);
        }

        setTotal(data.total);
        setHasMore(data.has_more);
      } else {
        if (!append) {
          setLots([]);
          setTotal(0);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching lots from backend:', error);
      if (!append) {
        setLots([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [projectId, project?.status, buildQueryParams]);

  // Initial fetch and filter changes
  useEffect(() => {
    if (projectId && project) {
      fetchLots(0, false);
    }
  }, [projectId, project, fetchLots]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!listContainerRef.current || loadingMore || !hasMore) return;

    const container = listContainerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;

    // Load more when user scrolls to bottom (with 100px threshold)
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchLots(lots.length, true);
    }
  }, [loadingMore, hasMore, lots.length, fetchLots]);

  // Attach scroll listener
  useEffect(() => {
    const container = listContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Handle lot selection from map (from parent component)
  useEffect(() => {
    if (onSelectLotFromMap) {
      onSelectLotFromMap.current = (lotId) => {
        setSelectedLotId(lotId);

        // Open sidebar if closed
        if (!isOpen) {
          setIsOpen(true);
        }

        // Scroll to lot card if already in the list
        const existingLot = lots.find(l => l.id === lotId);
        if (existingLot) {
          setTimeout(() => {
            const lotCard = lotCardRefs.current[lotId];
            if (lotCard && listContainerRef.current) {
              lotCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }
        // If not in list, the useEffect watching selectedLotData will add it
      };
    }
  }, [onSelectLotFromMap, lots, isOpen]);

  // When selectedLotData arrives from context, add it to the sidebar list if missing
  useEffect(() => {
    if (!selectedLotData || !selectedLotData.properties) return;

    const lotProps = selectedLotData.properties;
    const lotId = lotProps.id;
    if (!lotId) return;

    // Check if already in list
    const exists = lots.find(l => l.id === lotId);
    if (exists) return;

    // Build lot entry from context data (comes from fetchLotData)
    let centroid_lng = null;
    let centroid_lat = null;
    if (lotProps.centroid && lotProps.centroid.coordinates) {
      centroid_lng = lotProps.centroid.coordinates[0];
      centroid_lat = lotProps.centroid.coordinates[1];
    }

    const lotForList = {
      id: lotId,
      lot_number: lotProps.lot_number,
      status: lotProps.status || 'DISPONIBLE',
      area: lotProps.area_calculated || lotProps.area_declared || null,
      area_declared: lotProps.area_declared || null,
      area_calculated: lotProps.area_calculated || null,
      price: lotProps.price || null,
      currency: lotProps.currency || 'USD',
      block: lotProps.block || null,
      address: lotProps.address || null,
      centroid_lng,
      centroid_lat,
      ...lotProps
    };

    // Add to beginning of list
    setLots(prev => [lotForList, ...prev.filter(l => l.id !== lotId)]);

    // Scroll to the card after React renders it
    setTimeout(() => {
      const lotCard = lotCardRefs.current[lotId];
      if (lotCard && listContainerRef.current) {
        lotCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
  }, [selectedLotData, lots]);

  const handleLotClick = useCallback((lot) => {
    if (!onLotClick || !mapInstance) return;

    setSelectedLotId(lot.id);

    const triggerLotClick = () => {
      let geometry = lot.geometry;

      if (!geometry && mapInstance.isStyleLoaded()) {
        try {
          const features = mapInstance.querySourceFeatures('cloud4geo', {
            sourceLayer: 'lotes_areas',
            filter: ['==', ['get', 'id'], lot.id]
          });

          if (features.length > 0) {
            geometry = features[0].geometry;
          }
        } catch (error) {
          console.warn('Could not fetch geometry from map for lot:', lot.id, error);
        }
      }

      onLotClick({
        properties: lot,
        geometry: geometry
      });
    };

    if (lot.centroid_lng && lot.centroid_lat) {
      try {
        const onMoveEnd = () => {
          mapInstance.off('moveend', onMoveEnd);
          setTimeout(() => {
            triggerLotClick();
          }, 100);
        };

        mapInstance.on('moveend', onMoveEnd);

        mapInstance.flyTo({
          center: [lot.centroid_lng, lot.centroid_lat],
          zoom: 19,
          duration: 1000,
          essential: true
        });
      } catch (error) {
        console.warn('Could not zoom to lot centroid:', error);
        triggerLotClick();
      }
    } else {
      triggerLotClick();
    }
  }, [onLotClick, mapInstance, projectId]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'disponible':
      case 'libre':
        return 'bg-green-600';
      case 'reservado':
        return 'bg-yellow-500';
      case 'en_venta':
      case 'vendido':
      case 'sold':
      case 'devolucion':
        return 'bg-red-600';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'disponible':
      case 'libre':
        return 'Disponible';
      case 'reservado':
        return 'Reservado';
      case 'en_venta':
      case 'vendido':
      case 'sold':
      case 'devolucion':
        return 'Vendido';
      case 'no_disponible':
      case 'no disponible':
        return 'No Disponible';
      default:
        return status || 'No disponible';
    }
  };

  const hasActiveFilters = debouncedSearchTerm || statusFilter || minArea || maxArea || (project?.show_prices && (minPrice || maxPrice));

  const clearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStatusFilter('');
    setMinArea('');
    setMaxArea('');
    setMinPrice('');
    setMaxPrice('');
  };

  const handleReload = useCallback(() => {
    fetchLots(0, false);
    if (mapInstance && mapInstance.isStyleLoaded()) {
      const cacheBuster = `?t=${Date.now()}`;
      const sourcesToReload = ['cloud4geo', 'lotes-centroid', 'lotespro-areas', 'lotespro-areas-centroid', 'lotespro-lines', 'lotespro-markers'];
      sourcesToReload.forEach(sourceId => {
        const source = mapInstance.getSource(sourceId);
        if (source && source.setTiles) {
          const currentTiles = source.tiles || [];
          const refreshedTiles = currentTiles.map(url => url.replace(/(\?t=\d+)?$/, cacheBuster));
          source.setTiles(refreshedTiles);
        }
      });
    }
  }, [fetchLots, mapInstance]);

  return (
    <>
      {/* Toggle Button - Only show when sidebar is closed and on desktop */}
      {!isOpen && (
        <div className="hidden md:flex fixed left-4 top-[4.5rem] z-40 items-center space-x-1.5">
          <button
            onClick={() => setIsOpen(true)}
            className="flex bg-white hover:bg-gray-50 text-gray-700 rounded-lg shadow-md border border-gray-200 items-center transition-all duration-200 px-3 py-2 hover:shadow-lg"
            title="Mostrar lista de lotes"
          >
            <FontAwesomeIcon
              icon={faList}
              className="text-xs text-brand-600 mr-2"
            />
            <span className="text-xs font-semibold">Lotes</span>
            <span className="text-[10px] text-gray-400 ml-1.5">{total}</span>
          </button>
          <button
            onClick={handleReload}
            className={`flex bg-white hover:bg-gray-50 text-gray-500 hover:text-gray-700 rounded-lg shadow-md border border-gray-200 items-center justify-center transition-all duration-200 w-8 h-8 hover:shadow-lg ${loading ? 'opacity-50' : ''}`}
            title="Recargar lotes y mapa"
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSync} className={`text-xs ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      )}

      {/* Sidebar Panel - Desktop only */}
      <div
        className={`hidden md:block fixed left-0 top-16 h-[calc(100vh-4rem)] w-72 bg-white shadow-2xl z-30 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="bg-white border-b border-gray-200 shadow-theme-sm">
          <div className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center">
                <FontAwesomeIcon icon={faList} className="text-sm text-brand-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900 leading-tight">Lotes</h2>
                {project && (
                  <p className="text-xs text-gray-500 leading-tight">
                    {lots.length} de {total} {total === 1 ? 'lote' : 'lotes'}
                    {hasActiveFilters && (
                      <span className="text-brand-600"> (filtrado)</span>
                    )}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={handleReload}
                className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors hover:bg-gray-100 text-gray-600 hover:text-gray-900 ${loading ? 'opacity-50' : ''}`}
                title="Recargar lotes y mapa"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faSync} className={`text-xs ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${
                  showFilters || hasActiveFilters
                    ? 'bg-brand-100 text-brand-600 hover:bg-brand-200'
                    : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                }`}
                title="Mostrar/ocultar filtros"
              >
                <FontAwesomeIcon icon={faFilter} className="text-xs" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="w-7 h-7 rounded-md hover:bg-gray-100 flex items-center justify-center transition-colors text-gray-600 hover:text-gray-900"
                title="Cerrar lista de lotes"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
              </button>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-gray-50 border-b border-gray-200 p-3 space-y-3">
            {/* Status filter buttons - First for quick access */}
            <div className="grid grid-cols-4 gap-1.5">
              <button
                onClick={() => setStatusFilter('')}
                className={`py-1.5 rounded-md text-[11px] font-medium transition-colors text-center ${
                  statusFilter === ''
                    ? 'bg-gray-700 text-white'
                    : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => setStatusFilter(statusFilter === 'DISPONIBLE' ? '' : 'DISPONIBLE')}
                className={`py-1.5 rounded-md text-[11px] font-medium transition-colors text-center ${
                  statusFilter === 'DISPONIBLE'
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-green-700 border border-green-300 hover:bg-green-50'
                }`}
              >
                Disponible
              </button>
              <button
                onClick={() => setStatusFilter(statusFilter === 'RESERVADO' ? '' : 'RESERVADO')}
                className={`py-1.5 rounded-md text-[11px] font-medium transition-colors text-center ${
                  statusFilter === 'RESERVADO'
                    ? 'bg-yellow-500 text-white'
                    : 'bg-white text-yellow-700 border border-yellow-300 hover:bg-yellow-50'
                }`}
              >
                Reservado
              </button>
              <button
                onClick={() => setStatusFilter(statusFilter === 'VENDIDO' ? '' : 'VENDIDO')}
                className={`py-1.5 rounded-md text-[11px] font-medium transition-colors text-center ${
                  statusFilter === 'VENDIDO'
                    ? 'bg-red-600 text-white'
                    : 'bg-white text-red-700 border border-red-300 hover:bg-red-50'
                }`}
              >
                Vendido
              </button>
            </div>

            {/* Search by lot number */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                <FontAwesomeIcon icon={faSearch} className="mr-1 text-gray-500" />
                Buscar por número
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Ej: 15, 20, A1..."
                className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            {/* Area filters */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Área mín. (m²)
                </label>
                <input
                  type="number"
                  value={minArea}
                  onChange={(e) => setMinArea(e.target.value)}
                  placeholder="Ej: 100"
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Área máx. (m²)
                </label>
                <input
                  type="number"
                  value={maxArea}
                  onChange={(e) => setMaxArea(e.target.value)}
                  placeholder="Ej: 500"
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>

            {/* Price filters - Solo mostrar si show_prices está habilitado */}
            {project?.show_prices && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Precio mín. ({getCurrencySymbol(project?.currency)})
                  </label>
                  <input
                    type="number"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    placeholder="Ej: 50000"
                    min="0"
                    step="100"
                    className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Precio máx. ({getCurrencySymbol(project?.currency)})
                  </label>
                  <input
                    type="number"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    placeholder="Ej: 200000"
                    min="0"
                    step="100"
                    className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
              </div>
            )}

            {/* Clear filters button */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="w-full px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center space-x-1"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
                <span>Limpiar filtros</span>
              </button>
            )}
          </div>
        )}

        {/* Lots List */}
        <div
          ref={listContainerRef}
          className={`overflow-y-auto p-3 ${
            showFilters
              ? 'h-[calc(100%-17rem)]'
              : 'h-[calc(100%-4.5rem)]'
          }`}
        >
          {loading ? (
            <div className="text-center text-gray-500 mt-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-3"></div>
              <p>Cargando lotes...</p>
            </div>
          ) : total === 0 ? (
            <div className="text-center text-gray-500 mt-8">
              <FontAwesomeIcon icon={faList} className="text-4xl mb-3 opacity-30" />
              <p>No hay lotes disponibles</p>
            </div>
          ) : lots.length === 0 ? (
            <div className="text-center text-gray-500 mt-8">
              <FontAwesomeIcon icon={faFilter} className="text-4xl mb-3 opacity-30" />
              <p className="text-sm font-medium mb-1">No se encontraron lotes</p>
              <p className="text-xs text-gray-400">Intenta ajustar los filtros</p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-3 px-4 py-1.5 text-xs font-medium text-brand-600 bg-brand-50 border border-brand-200 rounded-md hover:bg-brand-100 transition-colors"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {lots.map((lot, index) => {
                const isSelected = selectedLotId === lot.id;
                return (
                  <div
                    key={lot.id || index}
                    ref={el => lotCardRefs.current[lot.id] = el}
                    onClick={() => handleLotClick(lot)}
                    className={`relative rounded-lg cursor-pointer transition-all duration-200 overflow-hidden border-2 ${
                      isSelected
                        ? 'border-[#ff9900] bg-orange-50 shadow-theme-sm'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-theme-xs'
                    }`}
                  >
                    {/* Status indicator stripe */}
                    <div className={`absolute top-0 left-0 w-0.5 h-full ${getStatusColor(lot.status)}`}></div>

                    {/* Card content */}
                    <div className={`p-2.5 pl-3 ${
                      isSelected ? 'bg-orange-50' : 'bg-white'
                    }`}>
                      {/* Header with lot number and status badge */}
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center space-x-1.5">
                          <span className={`text-sm font-bold ${
                            isSelected ? 'text-[#ff9900]' : 'text-gray-800'
                          }`}>
                            Lote {lot.lot_number || lot.lote_number || lot.numero || lot.name || lot.id || "N/A"}
                          </span>
                          {isSelected && (
                            <svg className="w-4 h-4 text-[#ff9900]" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${
                          lot.status?.toLowerCase() === 'disponible' || lot.status?.toLowerCase() === 'libre'
                            ? 'bg-green-100 text-green-800 border border-green-300'
                            : lot.status?.toLowerCase() === 'reservado'
                            ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                            : lot.status?.toLowerCase() === 'en_venta'
                            ? 'bg-blue-100 text-blue-800 border border-blue-300'
                            : lot.status?.toLowerCase() === 'vendido' || lot.status?.toLowerCase() === 'sold'
                            ? 'bg-red-100 text-red-800 border border-red-300'
                            : lot.status?.toLowerCase() === 'devolucion'
                            ? 'bg-purple-100 text-purple-800 border border-purple-300'
                            : 'bg-gray-100 text-gray-800 border border-gray-300'
                        }`}>
                          {getStatusText(lot.status)}
                        </span>
                      </div>

                      {/* Details grid */}
                      <div className="space-y-1">
                        {/* Mostrar manzana si existe */}
                        {(lot.block || lot.manzana) && (
                          <div className="flex items-center space-x-1.5 text-xs">
                            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <span className="text-gray-700 font-medium">
                              Manzana {lot.block || lot.manzana}
                            </span>
                          </div>
                        )}

                        {/* Mostrar dirección/ubicación si existe */}
                        {(lot.location || lot.address || lot.address_line) && (
                          <div className="flex items-center space-x-1.5 text-xs">
                            <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="text-gray-700 font-medium truncate">
                              {lot.location || lot.address || lot.address_line}
                            </span>
                          </div>
                        )}

                        {/* Mostrar área calculada (consistente con el filtro del backend) */}
                        {(lot.area_calculated || lot.area_declared || lot.area) && (
                          <div className="flex items-center space-x-1.5 text-xs">
                            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                            <span className="text-gray-700 font-medium">
                              {(lot.area_calculated || lot.area_declared || lot.area).toFixed(2)} m²
                            </span>
                          </div>
                        )}

                        {project?.show_prices && lot.price && (
                          <div className="flex items-center space-x-1.5">
                            <svg className="w-3.5 h-3.5 text-success-600" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs font-semibold text-success-700">
                              {getCurrencySymbol(lot.currency || project?.currency)} {lot.price.toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Loading more indicator */}
              {loadingMore && (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faSpinner} className="text-brand-600 animate-spin mr-2" />
                  <span className="text-xs text-gray-500">Cargando más lotes...</span>
                </div>
              )}

              {/* End of list indicator */}
              {!hasMore && lots.length > 0 && (
                <div className="text-center py-3 text-xs text-gray-400">
                  — Fin de la lista —
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LeftSidebar;
